import { useEffect, useState } from "react";
import { getReplyRatesByTag } from "interfaces/prospect";

interface ReplyRateData {
    tag: string;
    totalProspects: number;
    contactedProspects: number;
    respondedProspects: number;
    convertedProspects: number;
    contactRate: number;
    replyRate: number;
    conversionRate: number;
}

const ReplyRatesByTag = () => {
    const [data, setData] = useState<ReplyRateData[]>([]);
    const [loading, setLoading] = useState(true);

    const loadData = async () => {
        setLoading(true);
        try {
            const replyRatesData = await getReplyRatesByTag();
            setData(Array.isArray(replyRatesData) ? replyRatesData : []);
        } catch (error) {
            console.error("Error loading reply rates by tag:", error);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const formatPercentage = (value: number) => `${value.toFixed(1)}%`;

    const getTagBadgeColor = (tag: string) => {
        if (tag === "No Tag") return "bg-gray-100 text-gray-700";
        
        // Generate consistent colors based on tag
        const colors = [
            "bg-blue-100 text-blue-700",
            "bg-green-100 text-green-700", 
            "bg-purple-100 text-purple-700",
            "bg-orange-100 text-orange-700",
            "bg-pink-100 text-pink-700",
            "bg-indigo-100 text-indigo-700",
            "bg-red-100 text-red-700",
            "bg-yellow-100 text-yellow-700"
        ];
        
        const index = tag.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
        return colors[index];
    };

    if (loading) {
        return (
            <div className="w-full bg-white dark:bg-navy-800 rounded-lg shadow-lg p-6">
                <div className="animate-pulse">
                    <div className="h-6 bg-gray-200 dark:bg-navy-700 rounded w-1/3 mb-4"></div>
                    <div className="space-y-3">
                        {[...Array(5)].map((_, i) => (
                            <div key={i} className="h-12 bg-gray-200 dark:bg-navy-700 rounded"></div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full bg-white border dark:bg-navy-800 rounded-xs shadow-lg">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-navy-700">
                <h4 className="ml-px text-base font-bold text-navy-700 dark:text-white">
                    Reply Rates by Customer Tag
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                    Compare contact and response rates across different customer segments
                </p>
            </div>
            
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-navy-900">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Tag
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Total Prospects
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Contact Rate
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Reply Rate
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Conversion Rate
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Performance
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-navy-800 divide-y divide-gray-200 dark:divide-navy-700">
                        {data.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="px-6 py-8 text-center text-gray-500 dark:text-gray-300">
                                    No data available. Start tagging your prospects to see analytics.
                                </td>
                            </tr>
                        ) : (
                            data.map((item, index) => (
                                <tr key={index} className="hover:bg-gray-50 dark:hover:bg-navy-900">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTagBadgeColor(item.tag)}`}>
                                            {item.tag}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900 dark:text-white">
                                        {item.totalProspects}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                        <div className="text-sm text-gray-900 dark:text-white">
                                            {formatPercentage(item.contactRate)}
                                        </div>
                                        <div className="text-xs text-gray-500 dark:text-gray-300">
                                            {item.contactedProspects}/{item.totalProspects}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                                            {formatPercentage(item.replyRate)}
                                        </div>
                                        <div className="text-xs text-gray-500 dark:text-gray-300">
                                            {item.respondedProspects}/{item.contactedProspects}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                        <div className="text-sm text-gray-900 dark:text-white">
                                            {formatPercentage(item.conversionRate)}
                                        </div>
                                        <div className="text-xs text-gray-500 dark:text-gray-300">
                                            {item.convertedProspects}/{item.totalProspects}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-center">
                                        <div className="flex justify-center">
                                            <div className="w-16 bg-gray-200 rounded-full h-2 overflow-hidden">
                                                <div 
                                                    className={`h-2 rounded-full transition-all duration-300 ${
                                                        item.replyRate >= 30 ? 'bg-green-500' :
                                                        item.replyRate >= 15 ? 'bg-yellow-500' : 'bg-red-500'
                                                    }`}
                                                    style={{ width: `${Math.min(item.replyRate * 2, 100)}%` }}
                                                />
                                            </div>
                                        </div>
                                        <div className="text-xs text-gray-500 dark:text-gray-300 mt-1">
                                            {item.replyRate >= 30 ? 'Excellent' :
                                             item.replyRate >= 15 ? 'Good' : 'Needs Work'}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ReplyRatesByTag;